import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import { api } from "@/lib/api";
import { ArrowLeft, FileDown, Printer, Mail, MessageCircle, Copy, Edit, Trash2, Plus, IndianRupee, X } from "lucide-react";
import CurrencyInput from "@/components/CurrencyInput";

const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-700",
  pending: "bg-amber-100 text-amber-800",
  paid: "bg-emerald-100 text-emerald-800",
  partially_paid: "bg-sky-100 text-sky-800",
  cancelled: "bg-red-100 text-red-800",
  refunded: "bg-red-100 text-red-800",
};

const money = (n) => "₹ " + (n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export default function InvoiceView() {
  const { id } = useParams();
  const nav = useNavigate();
  const [inv, setInv] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => api.get(`/invoices/${id}`).then(({ data }) => { setInv(data); setEmailTo(data?.customer?.email || ""); }).catch(() => toast.error("Not found"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Payments
  const [ledger, setLedger] = useState({ payments: [], paid_total: 0, balance_due: 0, grand_total: 0 });
  const [payOpen, setPayOpen] = useState(false);
  const [pAmount, setPAmount] = useState("");
  const [pMethod, setPMethod] = useState("bank");
  const [pRef, setPRef] = useState("");
  const [pDate, setPDate] = useState(new Date().toISOString().slice(0, 10));
  const [pNotes, setPNotes] = useState("");
  const [savingPay, setSavingPay] = useState(false);

  const loadPayments = async () => {
    try { const { data } = await api.get(`/invoices/${id}/payments`); setLedger(data); }
    catch { /* ignore */ }
  };
  useEffect(() => { loadPayments(); /* eslint-disable-next-line */ }, [id]);

  const addPayment = async () => {
    const amount = parseFloat(pAmount);
    if (!amount || amount <= 0) return toast.error("Enter a valid amount");
    setSavingPay(true);
    try {
      const { data } = await api.post(`/invoices/${id}/payments`, {
        amount, method: pMethod, reference: pRef, paid_on: pDate, notes: pNotes,
      });
      setLedger(data);
      toast.success("Payment recorded");
      setPayOpen(false); setPAmount(""); setPRef(""); setPNotes("");
      load(); // refresh invoice status
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add");
    } finally { setSavingPay(false); }
  };

  const removePayment = async (pid) => {
    if (!window.confirm("Remove this payment entry?")) return;
    try {
      const { data } = await api.delete(`/invoices/${id}/payments/${pid}`);
      setLedger(data);
      toast.success("Removed");
      load();
    } catch { toast.error("Could not remove"); }
  };

  if (!inv) return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar role="agent" />
      <main className="flex-1 p-8 lg:p-12 text-slate-500 text-sm">Loading…</main>
    </div>
  );

  const openPdf = async () => {
    try {
      const res = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch { toast.error("Failed to open PDF"); }
  };
  const printInvoice = async () => {
    const res = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const w = window.open(url, "_blank");
    if (w) w.onload = () => w.print();
  };
  const duplicate = async () => {
    try {
      const { data } = await api.post(`/invoices/${id}/duplicate`);
      toast.success(`Duplicated as ${data.invoice_number}`);
      nav(`/invoices/${data.id}/edit`);
    } catch { toast.error("Could not duplicate"); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this invoice?")) return;
    await api.delete(`/invoices/${id}`);
    toast.success("Deleted");
    nav("/invoices");
  };
  const updateStatus = async (status) => {
    try {
      const { data } = await api.put(`/invoices/${id}/status`, { status });
      setInv(data);
      toast.success(`Marked ${status.replace("_", " ")}`);
    } catch { toast.error("Could not update"); }
  };
  const sendEmail = async () => {
    if (!emailTo) return toast.error("Enter recipient email");
    setSending(true);
    try {
      await api.post(`/invoices/${id}/email`, { to: emailTo });
      toast.success(`Sent to ${emailTo}`); setEmailOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };
  const shareWhatsApp = () => {
    const mobile = (inv.customer?.mobile || "").replace(/\D/g, "");
    const text = encodeURIComponent(`Invoice ${inv.invoice_number} — ${money(inv.summary?.grand_total)}\n${window.location.href}`);
    window.open(mobile ? `https://wa.me/${mobile}?text=${text}` : `https://wa.me/?text=${text}`, "_blank");
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar role="agent" />
      <main className="flex-1 p-8 lg:p-12">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <button onClick={() => nav("/invoices")} className="p-1.5 text-slate-500 hover:text-slate-800"><ArrowLeft size={18} /></button>
            <div>
              <div className="overline text-emerald-700">Invoice</div>
              <h1 className="font-editorial text-3xl text-slate-900">{inv.invoice_number}</h1>
            </div>
            <span className={`ml-2 text-[10.5px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-widest ${STATUS_STYLE[inv.status]}`}>
              {(inv.status || "draft").replace("_", " ")}
            </span>
            <div className="ml-auto flex flex-wrap gap-2">
              <button data-testid="inv-download-btn" onClick={openPdf} className="inline-flex items-center gap-1.5 border border-border rounded px-3 py-2 text-xs hover:border-[#16A34A]"><FileDown size={13} /> Download PDF</button>
              <button data-testid="inv-print-btn" onClick={printInvoice} className="inline-flex items-center gap-1.5 border border-border rounded px-3 py-2 text-xs hover:border-[#16A34A]"><Printer size={13} /> Print</button>
              <button data-testid="inv-email-btn" onClick={() => setEmailOpen(true)} className="inline-flex items-center gap-1.5 border border-border rounded px-3 py-2 text-xs hover:border-[#16A34A]"><Mail size={13} /> Email</button>
              <button data-testid="inv-whatsapp-btn" onClick={shareWhatsApp} className="inline-flex items-center gap-1.5 border border-border rounded px-3 py-2 text-xs hover:border-emerald-500"><MessageCircle size={13} /> WhatsApp</button>
              <button data-testid="inv-dup-btn" onClick={duplicate} className="inline-flex items-center gap-1.5 border border-border rounded px-3 py-2 text-xs hover:border-indigo-500"><Copy size={13} /> Duplicate</button>
              <Link data-testid="inv-edit-btn" to={`/invoices/${id}/edit`} className="inline-flex items-center gap-1.5 border border-border rounded px-3 py-2 text-xs hover:border-blue-500"><Edit size={13} /> Edit</Link>
              <button data-testid="inv-del-btn" onClick={remove} className="inline-flex items-center gap-1.5 border border-border rounded px-3 py-2 text-xs hover:border-red-500 hover:text-red-600"><Trash2 size={13} /> Delete</button>
            </div>
          </div>

          <div className="mb-4 flex flex-wrap gap-2 text-xs">
            <span className="text-slate-500">Quick status:</span>
            {["draft", "pending", "paid", "partially_paid", "cancelled", "refunded"].map((s) => (
              <button key={s} data-testid={`set-status-${s}`} onClick={() => updateStatus(s)} className={`px-2 py-1 rounded ${inv.status === s ? "bg-[#0F1E5B] text-white" : "border border-border text-slate-600 hover:border-emerald-500"}`}>
                {s.replace("_", " ")}
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <Card title="Bill to">
              <Line label="Name" value={inv.customer?.name} />
              <Line label="Company" value={inv.customer?.company_name} />
              <Line label="Address" value={inv.customer?.address} />
              <Line label="Email" value={inv.customer?.email} />
              <Line label="Mobile" value={inv.customer?.mobile} />
              <Line label="GSTIN" value={inv.customer?.gstin} />
              {inv.customer?.pan && <Line label="PAN" value={inv.customer?.pan} />}
              {inv.customer?.passport && <Line label="Passport" value={inv.customer?.passport} />}
            </Card>
            <Card title="Booking">
              <Line label="Booking ID" value={inv.booking?.booking_id} />
              <Line label="Quotation #" value={inv.booking?.quotation_number} />
              <Line label="Invoice Date" value={inv.booking?.invoice_date} />
              <Line label="Due Date" value={inv.booking?.due_date} />
              <Line label="Destination" value={inv.booking?.destination} />
              <Line label="Travel" value={`${inv.booking?.travel_from || "—"} → ${inv.booking?.travel_to || "—"}`} />
              <Line label="Sales Executive" value={inv.booking?.sales_executive} />
              <Line label="Pax" value={`${inv.booking?.adults || 0}A · ${inv.booking?.children || 0}C · ${inv.booking?.infants || 0}I`} />
            </Card>
          </div>

          <Card title="Tax summary">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-1 text-sm">
                <SRow k="Subtotal" v={money(inv.summary?.subtotal)} />
                {inv.summary?.service_charges > 0 && <SRow k="Service Charges" v={money(inv.summary.service_charges)} />}
                {inv.summary?.gst_amount > 0 && (inv.tax?.interstate ? (
                  <SRow k={`IGST @ ${inv.tax?.gst_rate}%`} v={money(inv.summary.igst)} />
                ) : (
                  <>
                    <SRow k={`CGST @ ${(inv.tax?.gst_rate || 0) / 2}%`} v={money(inv.summary.cgst)} />
                    <SRow k={`SGST @ ${(inv.tax?.gst_rate || 0) / 2}%`} v={money(inv.summary.sgst)} />
                  </>
                ))}
                {inv.summary?.tcs_amount > 0 && <SRow k={`TCS @ ${inv.tax?.tcs_rate}%`} v={money(inv.summary.tcs_amount)} />}
                {inv.summary?.round_off !== 0 && <SRow k="Round Off" v={money(inv.summary.round_off)} />}
                {ledger.paid_total > 0 && <SRow k="Paid so far" v={<span className="text-emerald-700 font-semibold">{money(ledger.paid_total)}</span>} />}
              </div>
              <div>
                <div className="bg-[#0F1E5B] text-white rounded p-5">
                  <div className="text-[10.5px] uppercase tracking-widest text-emerald-300">Grand Total</div>
                  <div className="font-editorial text-3xl">{money(inv.summary?.grand_total)}</div>
                  <div className="text-[11px] text-emerald-200 mt-2 leading-relaxed">{inv.summary?.amount_in_words}</div>
                </div>
                {ledger.balance_due > 0 && ledger.paid_total > 0 && (
                  <div data-testid="balance-due-card" className="mt-3 bg-amber-100 border border-amber-300 rounded p-4">
                    <div className="text-[10.5px] uppercase tracking-widest text-amber-800">Balance due</div>
                    <div className="font-editorial text-2xl text-amber-900">{money(ledger.balance_due)}</div>
                  </div>
                )}
                {ledger.paid_total > 0 && ledger.balance_due === 0 && (
                  <div data-testid="paid-in-full-card" className="mt-3 bg-emerald-100 border border-emerald-300 rounded p-4">
                    <div className="text-[10.5px] uppercase tracking-widest text-emerald-800">Paid in full</div>
                    <div className="font-editorial text-lg text-emerald-900">Thank you!</div>
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Payment ledger */}
          <Card title="Payment ledger">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs text-slate-500">
                {ledger.payments.length === 0 ? "No payments recorded yet." : `${ledger.payments.length} payment${ledger.payments.length === 1 ? "" : "s"} on file`}
              </div>
              <button data-testid="pay-add-btn" onClick={() => setPayOpen(true)} className="inline-flex items-center gap-1.5 text-xs bg-[#16A34A] hover:bg-emerald-700 text-white px-3 py-1.5 rounded">
                <Plus size={12} /> Record payment
              </button>
            </div>
            {ledger.payments.length > 0 && (
              <table className="w-full text-sm">
                <thead className="text-[10.5px] text-slate-500 uppercase tracking-widest">
                  <tr>
                    <th className="text-left py-2">Date</th>
                    <th className="text-left py-2">Method</th>
                    <th className="text-left py-2">Reference</th>
                    <th className="text-right py-2">Amount</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.payments.map((p) => (
                    <tr key={p.id} data-testid={`pay-row-${p.id}`} className="border-t border-border">
                      <td className="py-2 text-slate-700">{p.paid_on || "—"}</td>
                      <td className="py-2 text-slate-700 capitalize">{p.method}</td>
                      <td className="py-2 text-slate-600 text-xs">{p.reference || "—"}</td>
                      <td className="py-2 text-right font-mono text-slate-900">{money(p.amount)}</td>
                      <td className="text-right">
                        <button data-testid={`pay-del-${p.id}`} onClick={() => removePayment(p.id)} className="p-1 text-slate-400 hover:text-red-600"><Trash2 size={13} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          {payOpen && (
            <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setPayOpen(false)}>
              <div className="bg-white rounded-lg p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="font-editorial text-xl flex-1">Record a payment</div>
                  <button onClick={() => setPayOpen(false)}><X size={16} className="text-slate-500" /></button>
                </div>
                <div className="text-xs text-slate-500 mb-4">Balance due: <b className="text-slate-800">{money(ledger.balance_due || inv.summary?.grand_total || 0)}</b></div>
                <div className="space-y-3">
                  <label className="text-xs block">
                    <div className="uppercase tracking-widest text-slate-500 mb-1">Amount (₹)</div>
                    <CurrencyInput
                      testid="pay-amount"
                      value={pAmount === "" ? undefined : Number(pAmount)}
                      onChange={(v) => setPAmount(String(v))}
                    />
                    <button onClick={() => setPAmount(String(ledger.balance_due || 0))} className="text-[11px] text-emerald-700 mt-1 hover:underline">Fill balance due</button>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="text-xs">
                      <div className="uppercase tracking-widest text-slate-500 mb-1">Method</div>
                      <select data-testid="pay-method" value={pMethod} onChange={(e) => setPMethod(e.target.value)} className="w-full border border-border rounded px-2 py-2 text-sm bg-white">
                        {["cash", "bank", "upi", "card", "cheque", "razorpay", "other"].map(m => <option key={m} value={m}>{m.toUpperCase()}</option>)}
                      </select>
                    </label>
                    <label className="text-xs">
                      <div className="uppercase tracking-widest text-slate-500 mb-1">Received on</div>
                      <input data-testid="pay-date" type="date" value={pDate} onChange={(e) => setPDate(e.target.value)} className="w-full border border-border rounded px-2 py-2 text-sm" />
                    </label>
                  </div>
                  <label className="text-xs block">
                    <div className="uppercase tracking-widest text-slate-500 mb-1">Reference / Txn ID</div>
                    <input data-testid="pay-ref" value={pRef} onChange={(e) => setPRef(e.target.value)} placeholder="Cheque #, UPI ref, bank txn…" className="w-full border border-border rounded px-3 py-2 text-sm" />
                  </label>
                  <label className="text-xs block">
                    <div className="uppercase tracking-widest text-slate-500 mb-1">Notes</div>
                    <textarea rows={2} value={pNotes} onChange={(e) => setPNotes(e.target.value)} className="w-full border border-border rounded px-3 py-2 text-sm" />
                  </label>
                </div>
                <div className="flex gap-2 mt-5 justify-end">
                  <button onClick={() => setPayOpen(false)} className="text-xs px-3 py-2 border border-border rounded">Cancel</button>
                  <button data-testid="pay-save" onClick={addPayment} disabled={savingPay} className="text-xs px-3 py-2 bg-[#16A34A] text-white rounded disabled:opacity-60 inline-flex items-center gap-1.5">
                    <IndianRupee size={12} /> {savingPay ? "Saving…" : "Save payment"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {emailOpen && (
            <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setEmailOpen(false)}>
              <div className="bg-white rounded-lg p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                <div className="font-editorial text-xl mb-2">Email this invoice</div>
                <div className="text-xs text-slate-500 mb-4">A copy of the PDF will be attached.</div>
                <label className="text-xs block">
                  <div className="uppercase tracking-widest text-slate-500 mb-1">To</div>
                  <input data-testid="email-to" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} className="w-full border border-border rounded px-3 py-2 text-sm" />
                </label>
                <div className="flex gap-2 mt-4 justify-end">
                  <button onClick={() => setEmailOpen(false)} className="text-xs px-3 py-2 border border-border rounded">Cancel</button>
                  <button data-testid="email-send" onClick={sendEmail} disabled={sending} className="text-xs px-3 py-2 bg-[#16A34A] text-white rounded disabled:opacity-60">{sending ? "Sending…" : "Send"}</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white border border-border rounded p-5 mb-4">
      <div className="overline mb-3">{title}</div>
      {children}
    </div>
  );
}
function Line({ label, value }) {
  if (!value) return null;
  return <div className="flex text-sm py-0.5"><div className="text-slate-500 w-32">{label}</div><div className="text-slate-800 flex-1">{value}</div></div>;
}
function SRow({ k, v }) {
  return <div className="flex justify-between"><span className="text-slate-500">{k}</span><span className="font-mono text-slate-800">{v}</span></div>;
}
